<template>
  <div class="tmap" :style="height ? { height } : undefined">
    <div ref="mapContainer" class="tian-di-tu-map"></div>
    <div class="search" v-if="enableSearch">
      <el-input
        v-model="searchText"
        style="max-width: 300px"
        placeholder="请输入搜索内容"
        class="input-with-select"
      >
        <template #append>
          <Icon @click="searchMap" icon="material-symbols:search" />
        </template>
      </el-input>
      <div class="search_result" v-if="searchResult.length > 0">
        <div class="search_box" v-for="item in searchResult" :key="item.hotPointID">
          <div class="search_title">
            <span class="search_name">{{ item.name }}</span>
            <span class="search_address">{{ item.address }}</span>
          </div>
          <div class="search_btn">
            <BaseButton type="primary" link size="small" @click="selectMap(item)">选择</BaseButton>
          </div>
        </div>
      </div>
    </div>
    <div class="pain_map" v-if="enablePolygon">
      <BaseButton type="primary" size="small" @click="drawingRange">绘制范围</BaseButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, onBeforeUnmount, watch, nextTick, computed } from 'vue'
import { ElInput } from 'element-plus'
import { useIcon } from '@/hooks/web/useIcon'
import { getDefaultMapCenter } from '@/utils/chinaCities'
import { loadTiandituSdk } from '@/utils/loadTianditu'
import { reverseGeocodeViaJsApi } from '@/utils/tiandituWebService'

const emit = defineEmits<{
  (e: 'marker-added', marker: Record<string, any>): void
  /** 与原 TencentMap 对齐：左键选点后回传坐标与地址 */
  (e: 'marker-change', data: { position: { lat: number; lng: number }; address: string }): void
  /** 通学公交车辆标记点击 */
  (e: 'school-bus-marker-click', data: { txVehicleId: string; lng: number; lat: number }): void
  /** 网约车/出租车车辆状态标记点击 */
  (e: 'ride-vehicle-marker-click', data: { driverId: string; lng: number; lat: number }): void
}>()

const { icon } = useIcon({ icon: 'material-symbols:search' })

// 定义组件属性
const props = defineProps({
  // 地图的中心点
  center: {
    type: Object,
    default: () => getDefaultMapCenter(import.meta.env.VITE_CITY)
  },
  // 地图的缩放级别
  zoom: {
    type: Number,
    default: 11
  },
  // 容器高度（组织机构/门店等表单场景）
  height: {
    type: String,
    default: ''
  },
  // 是否启用搜索功能
  enableSearch: {
    type: Boolean,
    default: true
  },
  // 是否启用右键标记功能
  enableRightClickMarker: {
    type: Boolean,
    default: true
  },
  // 是否开启绘制范围功能
  enablePolygon: {
    type: Boolean,
    default: true
  },
  // 鼠标悬停地图时是否允许滚轮缩放（在途监控等场景需开启）
  enableScrollWheelZoom: {
    type: Boolean,
    default: false
  },
  /** 兼容原 TencentMap：是否允许滚轮缩放 */
  enableScrollWheel: {
    type: Boolean,
    default: undefined
  },
  /** 兼容原 TencentMap：是否允许拖动 */
  enableDrag: {
    type: Boolean,
    default: true
  },
  /** 兼容原 TencentMap：左键点击选点 */
  enableMarker: {
    type: Boolean,
    default: false
  }
})

/** 滚轮缩放：优先 enableScrollWheel（腾讯兼容），否则用 enableScrollWheelZoom */
const scrollWheelEnabled = computed(() => {
  if (props.enableScrollWheel !== undefined) return !!props.enableScrollWheel
  return !!props.enableScrollWheelZoom
})

// 地图相关引用和变量
const mapContainer = ref<HTMLDivElement | null>(null)
let mapData: any = null
let T: any = null
let searchMapPoi: any = null
let polygonToolHandler: any = null
let clickHandler: ((e: any) => void) | null = null

// 搜索相关数据
const searchText = ref('')
const searchResult = ref<any[]>([])
const searchSelectData = ref<any>({})

// 监听城市变化：只平移中心，保留当前缩放（避免覆盖选中车辆的约 50 米视野）
watch(
  () => props.center,
  (center) => {
    if (!mapData || !T || center?.lng == null || center?.lat == null) return
    const centerPoint = new T.LngLat(center.lng, center.lat)
    try {
      if (typeof mapData.panTo === 'function') {
        mapData.panTo(centerPoint)
      } else {
        const z = typeof mapData.getZoom === 'function' ? mapData.getZoom() : props.zoom
        mapData.centerAndZoom(centerPoint, z)
      }
    } catch {
      /* ignore */
    }
  }
)
watch(
  () => props.enableRightClickMarker,
  () => {
    if (props.enableRightClickMarker) {
      enableRightClickMarkerFunction()
    } else {
      disableRightClickMarkerFunction()
    }
  }
  // { immediate: true }
)

watch(
  () => props.enableMarker,
  (enable) => {
    if (!mapData) return
    if (enable) {
      bindLeftClickMarker()
    } else {
      unbindLeftClickMarker()
    }
  }
)

watch(scrollWheelEnabled, (enable) => {
  if (!mapData) return
  if (enable) {
    mapData.enableScrollWheelZoom()
  } else {
    mapData.disableScrollWheelZoom()
  }
})

watch(
  () => props.enableDrag,
  (enable) => {
    if (!mapData) return
    if (enable) {
      mapData.enableDrag?.()
    } else {
      mapData.disableDrag?.()
    }
  }
)

// 地图初始化
const initMap = () => {
  if (!mapContainer.value) return

  // 确保天地图API已加载
  if (typeof window.T !== 'undefined') {
    T = window.T

    // 检查必要的API是否存在
    if (!checkApiAvailability()) {
      console.error('天地图API加载不完整')
      return
    }

    // 创建地图对象
    mapData = new T.Map(mapContainer.value, {
      style: TMAP_NORMAL_MAP
    })

    // 初始化地图中心点和缩放级别
    const centerPoint = new T.LngLat(props.center.lng, props.center.lat)
    mapData.centerAndZoom(centerPoint, props.zoom)
    // 默认关闭滚轮缩放，避免页面滚动误触；在途监控等可通过 prop 开启
    if (scrollWheelEnabled.value) {
      mapData.enableScrollWheelZoom()
    } else {
      mapData.disableScrollWheelZoom()
    }

    if (props.enableDrag === false) {
      mapData.disableDrag?.()
    }

    // 添加地图控件
    addMapControls()

    // 判断是否可以启用标记功能
    if (props.enableRightClickMarker) {
      // 延迟执行确保地图完全初始化
      setTimeout(() => {
        if (mapData) {
          enableRightClickMarkerFunction()
        }
      }, 100)
    }

    // 左键选点（组织机构/租赁公司等表单）
    if (props.enableMarker) {
      bindLeftClickMarker()
    }

    console.log('地图初始化成功')
  } else {
    // 如果API还未加载完成，等待一段时间后重试
    console.log('等待天地图API加载...')
    setTimeout(initMap, 500)
  }
}
// 检测当前地图中心点是否和传入地址一样
const getCenter = () => {
  setTimeout(() => {
    if (mapData.getCenter().lat !== props.center.lat) {
      const centerPoint = new T.LngLat(props.center.lng, props.center.lat)
      mapData.centerAndZoom(centerPoint, props.zoom)
    }
  }, 200)
}

// 检查API可用性
const checkApiAvailability = () => {
  return T.Map && T.LngLat && T.Marker
}

// 添加地图控件
const addMapControls = () => {
  if (T.Control && T.Control.Zoom) {
    mapData.addControl(new T.Control.Zoom())
  }
  // 确保地图不显示网格
  try {
    // 尝试设置地图样式，禁用网格
    if (mapData.setMapStyle) {
      mapData.setMapStyle({
        style: 'normal', // 使用标准样式
        features: ['road', 'building', 'water', 'land'], // 只显示这些要素，避免网格
        version: 8
      })
    }
  } catch (e) {
    console.warn('设置地图样式失败:', e)
  }
}

// 启用右键标记功能
const enableRightClickMarkerFunction = () => {
  if (!mapData || !T) {
    console.warn('右键标记功能API不完整')
    return
  }

  try {
    const obj = {
      text: '标记',
      isDisable: false,
      callback: function (lnglat) {
        if (props.enableRightClickMarker) {
          const marker = new T.Marker(new T.LngLat(lnglat.getLng(), lnglat.getLat()))
          mapData.clearOverLays()
          //向地图上添加标注
          mapData.addOverLay(marker)
          getMarkerAddressInfo(lnglat.getLng(), lnglat.getLat())
        }
      }
    }
    const menu = new T.ContextMenu({ width: 140 })
    const item = new T.MenuItem(obj.text, obj.callback)
    menu.addItem(item)
    mapData.addContextMenu(menu)
    mapData.clearOverLays()
  } catch (e) {
    console.error('右键标记功能初始化失败:', e)
  }
}
// 禁用右键标记功能
const disableRightClickMarkerFunction = () => {
  if (!mapData || !T) {
    console.warn('右键标记功能API不完整')
    return
  }

  // 安全检查：确保 removeContextMenu 方法存在
  if (mapData.removeContextMenu && typeof mapData.removeContextMenu === 'function') {
    try {
      mapData.removeContextMenu()
    } catch (e) {
      console.warn('移除右键菜单失败:', e)
    }
  } else {
    console.warn('removeContextMenu 方法不可用')
  }
}

// 新增函数：获取标记点地址信息
const getMarkerAddressInfo = async (lng: number, lat: number) => {
  const markerData: Record<string, any> = {
    lng,
    lat,
    address: '未知地址',
    province: '',
    county: '',
    city: ''
  }
  try {
    const parsed = await reverseGeocodeViaJsApi(lng, lat)
    if (parsed) {
      const addressInfo = parsed.addressComponent
      markerData.address = parsed.formattedAddress || addressInfo?.address || '未知地址'
      markerData.province = addressInfo?.province || ''
      markerData.county = addressInfo?.county || addressInfo?.district || ''
      markerData.city = addressInfo?.city || ''
    }
  } catch (e) {
    console.error('获取地址信息失败:', e)
  }

  emit('marker-added', markerData)
  emit('marker-change', {
    position: { lat, lng },
    address: markerData.address || ''
  })
}

/** 左键选点（兼容原腾讯地图交互） */
const bindLeftClickMarker = () => {
  if (!mapData || clickHandler) return
  clickHandler = (e: any) => {
    if (!props.enableMarker) return
    const lnglat = e?.lnglat
    if (!lnglat) return
    const lng = typeof lnglat.getLng === 'function' ? lnglat.getLng() : lnglat.lng
    const lat = typeof lnglat.getLat === 'function' ? lnglat.getLat() : lnglat.lat
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return
    mapData.clearOverLays()
    mapData.addOverLay(new T.Marker(new T.LngLat(lng, lat)))
    getMarkerAddressInfo(lng, lat)
  }
  mapData.addEventListener('click', clickHandler)
}

const unbindLeftClickMarker = () => {
  if (!mapData || !clickHandler) return
  try {
    mapData.removeEventListener('click', clickHandler)
  } catch {
    /* ignore */
  }
  clickHandler = null
}

/** 兼容原 TencentMap：创建标记 */
const createMarker = (position: { lat: number; lng: number }) => {
  if (!position || !Number.isFinite(position.lat) || !Number.isFinite(position.lng)) return
  setMarkerEcho(position.lng, position.lat, false)
}

/** 兼容原 TencentMap：清除标记 */
const clearMarker = () => {
  clearMarkers()
}

/** 兼容原 TencentMap：设置中心 */
const setMapCenter = (position: { lat: number; lng: number }) => {
  if (!mapData || !T) return
  if (!Number.isFinite(position?.lat) || !Number.isFinite(position?.lng)) return
  mapData.centerAndZoom(new T.LngLat(position.lng, position.lat), props.zoom)
}

// 设置标记点回显lng--经度, lat--纬度；fitView=false 时仅更新标记，不重置中心与缩放
const setMarkerEcho = (lng, lat, fitView = true) => {
  if (!lng || !T) {
    console.warn('标记点回显API不完整')
    return
  }
  mapData.clearOverLays()
  //创建标注对象
  const marker = new T.Marker(new T.LngLat(lng, lat))
  //向地图上添加标注
  mapData.addOverLay(marker)
  if (fitView) {
    const centerPoint = new T.LngLat(lng, lat)
    mapData.centerAndZoom(centerPoint, props.zoom)
  }
}

/**
 * 通学公交车辆标记回显（自定义色图标，默认 #fbbf24）
 * @param fitView 是否定位并重置缩放（定时刷新时应传 false，避免打断用户缩放）
 * @returns 是否成功绘制
 */
const setSchoolBusMarkerEcho = (lng, lat, color = '#fbbf24', fitView = true) => {
  return setSchoolBusMarkers([{ lng, lat, color }], { fitView, centerLng: lng, centerLat: lat })
}

/** 通学车辆覆盖物句柄：只移除自身，禁止 clearOverLays（缩放动画中 clear 易丢点） */
let schoolBusOverlays = []
/** 网约车/出租车车辆状态车图标覆盖物 */
let rideVehicleOverlays = []

const clearSchoolBusOverlays = () => {
  if (!mapData) {
    schoolBusOverlays = []
    return
  }
  schoolBusOverlays.forEach((overlay) => {
    try {
      mapData.removeOverLay(overlay)
    } catch {
      /* ignore */
    }
  })
  schoolBusOverlays = []
}

const clearRideVehicleOverlays = () => {
  if (!mapData) {
    rideVehicleOverlays = []
    return
  }
  rideVehicleOverlays.forEach((overlay) => {
    try {
      mapData.removeOverLay(overlay)
    } catch {
      /* ignore */
    }
  })
  rideVehicleOverlays = []
}

/** 缓存车辆点位，缩放时按级别重绘图标尺寸 */
let rideVehiclePointsCache = []
let rideVehicleZoomBound = false
let rideVehicleZoomTimer = null
let rideVehicleZoomPollTimer = null
let rideVehicleLastZoom = null

/**
 * 按地图缩放计算车图标像素（整体比上一版大约 1～2 号；放大级别明显变大）
 * zoom≈12 → 高约 30；zoom≈15 → 高约 44；zoom≈17 → 高约 56
 */
const rideIconSizeByZoom = (zoom, selected) => {
  const z = Number(zoom)
  const level = Number.isFinite(z) ? z : 12
  let h = 22 + Math.max(0, level - 10) * 4.8
  h = Math.max(24, Math.min(64, h))
  if (selected) h = Math.round(h * 1.18)
  else h = Math.round(h)
  const w = Math.max(12, Math.round(h * 0.52))
  return { w, h }
}

const refreshRideVehicleMarkersByZoom = () => {
  if (!mapData || !rideVehiclePointsCache.length) return
  const z = typeof mapData.getZoom === 'function' ? mapData.getZoom() : null
  if (z == null) return
  if (rideVehicleLastZoom != null && Math.abs(Number(z) - Number(rideVehicleLastZoom)) < 0.15) {
    return
  }
  setRideVehicleMarkers(rideVehiclePointsCache, { fitView: false, _fromZoom: true })
}

const bindRideVehicleZoomListener = () => {
  if (!mapData || rideVehicleZoomBound) return
  rideVehicleZoomBound = true
  const onZoom = () => {
    if (rideVehicleZoomTimer) clearTimeout(rideVehicleZoomTimer)
    // 天地图缩放动画结束后再取级别重绘
    rideVehicleZoomTimer = setTimeout(() => {
      refreshRideVehicleMarkersByZoom()
    }, 80)
  }
  // 兼容不同版本事件名
  ;['zoomend', 'zoom', 'moveend'].forEach((evt) => {
    try {
      mapData.addEventListener(evt, onZoom)
    } catch {
      /* ignore */
    }
  })
  // 兜底：部分环境 zoomend 不触发时，轮询 zoom 变化
  if (rideVehicleZoomPollTimer) clearInterval(rideVehicleZoomPollTimer)
  rideVehicleZoomPollTimer = setInterval(() => {
    if (!rideVehiclePointsCache.length) return
    refreshRideVehicleMarkersByZoom()
  }, 400)
}

/** 生成车辆针形图标：沿用原水滴造型，仅去描边、不换图标 */
const buildSchoolBusIcon = (color, selected) => {
  const w = selected ? 48 : 32
  const h = selected ? 60 : 40
  // 与改造前相同的水滴 path，去掉 stroke 描边
  const svg = selected
    ? `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="60" viewBox="0 0 48 60">
        <circle cx="24" cy="22" r="20" fill="${color}" opacity="0.28"/>
        <path fill="${color}"
          d="M24 2C12.4 2 3 11.4 3 23c0 16 21 35 21 35s21-19 21-35C45 11.4 35.6 2 24 2z"/>
        <circle cx="24" cy="23" r="8" fill="#fff"/>
      </svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
        <path fill="${color}"
          d="M16 0C7.2 0 0 7.2 0 16c0 12 16 24 16 24s16-12 16-24C32 7.2 24.8 0 16 0z"/>
        <circle cx="16" cy="16" r="6" fill="#fff"/>
      </svg>`
  return {
    url: `data:image/svg+xml,${encodeURIComponent(svg)}`,
    w,
    h
  }
}

/**
 * 批量绘制通学车辆标记（只替换通学覆盖物，不清空整图）。
 */
const setSchoolBusMarkers = (points, opts = {}) => {
  if (!T || !mapData) {
    console.warn('通学批量标记 API 不完整')
    return false
  }
  const list = (points || []).filter(
    (p) =>
      p != null &&
      Number.isFinite(Number(p.lng)) &&
      Number.isFinite(Number(p.lat)) &&
      Math.abs(Number(p.lng)) <= 180 &&
      Math.abs(Number(p.lat)) <= 90
  )
  clearSchoolBusOverlays()
  if (!list.length) {
    return false
  }
  try {
    const ordered = [...list].sort((a, b) => Number(!!a.selected) - Number(!!b.selected))
    const seen = new Map()
    ordered.forEach((p) => {
      const key = Number(p.lng).toFixed(5) + '_' + Number(p.lat).toFixed(5)
      const dup = seen.get(key) || 0
      seen.set(key, dup + 1)
      const lng = Number(p.lng) + (dup > 0 ? dup * 0.00035 : 0)
      const lat = Number(p.lat) + (dup > 0 ? dup * 0.00025 : 0)
      const selected = !!p.selected
      const color = p.color || (selected ? '#fbbf24' : '#64748b')
      const iconInfo = buildSchoolBusIcon(color, selected)
      let marker
      if (iconInfo && T.Icon) {
        const icon = new T.Icon({
          iconUrl: iconInfo.url,
          iconSize: new T.Point(iconInfo.w, iconInfo.h),
          iconAnchor: new T.Point(iconInfo.w / 2, iconInfo.h)
        })
        marker = new T.Marker(new T.LngLat(lng, lat), { icon })
      } else {
        marker = new T.Marker(new T.LngLat(lng, lat))
      }
      if (p.id) {
        const vehicleId = String(p.id)
        const rawLng = Number(p.lng)
        const rawLat = Number(p.lat)
        marker.addEventListener('click', () => {
          setTimeout(() => {
            emit('school-bus-marker-click', {
              txVehicleId: vehicleId,
              lng: rawLng,
              lat: rawLat
            })
          }, 0)
        })
      }
      mapData.addOverLay(marker)
      schoolBusOverlays.push(marker)
    })
    if (opts.fitView !== false) {
      const cLng = opts.centerLng ?? list.find((p) => p.selected)?.lng ?? list[0].lng
      const cLat = opts.centerLat ?? list.find((p) => p.selected)?.lat ?? list[0].lat
      const zoomLevel = opts.zoom != null ? opts.zoom : props.zoom
      mapData.centerAndZoom(new T.LngLat(Number(cLng), Number(cLat)), zoomLevel)
      if (opts.zoom != null && typeof mapData.setZoom === 'function') {
        mapData.setZoom(opts.zoom)
      }
    }
    return true
  } catch (e) {
    console.warn('通学批量标记失败', e)
    return false
  }
}

/**
 * 批量绘制网约车/出租车车辆图标（俯视图 PNG）。
 * points: { id, lng, lat, iconUrl, selected? }
 * 图标随地图 zoom 缩放：城市级更小，放大后略增大。
 */
const setRideVehicleMarkers = (points, opts = {}) => {
  if (!T || !mapData) {
    console.warn('车辆状态标记 API 不完整')
    return false
  }
  const list = (points || []).filter(
    (p) =>
      p != null &&
      Number.isFinite(Number(p.lng)) &&
      Number.isFinite(Number(p.lat)) &&
      Math.abs(Number(p.lng)) <= 180 &&
      Math.abs(Number(p.lat)) <= 90
  )
  if (!opts._fromZoom) {
    rideVehiclePointsCache = list.map((p) => ({ ...p }))
  }
  clearRideVehicleOverlays()
  if (!list.length) {
    return false
  }
  try {
    bindRideVehicleZoomListener()
    const zoom =
      typeof mapData.getZoom === 'function' ? mapData.getZoom() : opts.zoom != null ? opts.zoom : props.zoom
    rideVehicleLastZoom = zoom
    const ordered = [...list].sort((a, b) => Number(!!a.selected) - Number(!!b.selected))
    const seen = new Map()
    ordered.forEach((p) => {
      const key = Number(p.lng).toFixed(5) + '_' + Number(p.lat).toFixed(5)
      const dup = seen.get(key) || 0
      seen.set(key, dup + 1)
      const lng = Number(p.lng) + (dup > 0 ? dup * 0.00035 : 0)
      const lat = Number(p.lat) + (dup > 0 ? dup * 0.00025 : 0)
      const selected = !!p.selected
      const { w: iconW, h: iconH } = rideIconSizeByZoom(zoom, selected)
      let marker
      if (p.iconUrl && T.Icon) {
        const icon = new T.Icon({
          iconUrl: p.iconUrl,
          iconSize: new T.Point(iconW, iconH),
          iconAnchor: new T.Point(iconW / 2, iconH / 2)
        })
        marker = new T.Marker(new T.LngLat(lng, lat), { icon })
      } else {
        marker = new T.Marker(new T.LngLat(lng, lat))
      }
      if (p.id) {
        const driverId = String(p.id)
        const rawLng = Number(p.lng)
        const rawLat = Number(p.lat)
        marker.addEventListener('click', () => {
          setTimeout(() => {
            emit('ride-vehicle-marker-click', {
              driverId,
              lng: rawLng,
              lat: rawLat
            })
          }, 0)
        })
      }
      mapData.addOverLay(marker)
      rideVehicleOverlays.push(marker)
    })
    if (opts.fitView === true) {
      const cLng = opts.centerLng ?? list.find((p) => p.selected)?.lng ?? list[0].lng
      const cLat = opts.centerLat ?? list.find((p) => p.selected)?.lat ?? list[0].lat
      const zoomLevel = opts.zoom != null ? opts.zoom : props.zoom
      mapData.centerAndZoom(new T.LngLat(Number(cLng), Number(cLat)), zoomLevel)
    }
    return true
  } catch (e) {
    console.warn('车辆状态标记失败', e)
    return false
  }
}

/**
 * 将地图居中到指定坐标并设置缩放级别（如选中车辆约 50 米）。
 */
const centerAndZoomTo = (lng, lat, zoom) => {
  if (!T || !mapData || !Number.isFinite(Number(lng)) || !Number.isFinite(Number(lat))) return false
  try {
    const z = zoom != null ? zoom : props.zoom
    const point = new T.LngLat(Number(lng), Number(lat))
    if (typeof mapData.setZoom === 'function') {
      mapData.setZoom(z)
    }
    if (typeof mapData.panTo === 'function') {
      mapData.panTo(point)
    }
    mapData.centerAndZoom(point, z)
    return true
  } catch (e) {
    console.warn('地图居中缩放失败', e)
    return false
  }
}

/**
 * 视野稳定后再执行（仅用延时；moveend 会在缩放动画中过早触发，随后动画清掉刚画的点）。
 */
const afterMapViewSettled = (fn, delayMs = 350) => {
  if (typeof fn !== 'function') return
  setTimeout(() => {
    try {
      fn()
    } catch (e) {
      console.warn('afterMapViewSettled 回调失败', e)
    }
  }, delayMs)
}

// 绘制范围功能
const drawingRange = () => {
  if (!mapData || !T) {
    console.warn('绘制功能API不完整')
    return
  }

  const config = {
    showLabel: true,
    color: 'blue',
    weight: 3,
    opacity: 0.5,
    fillColor: '#409eff',
    fillOpacity: 0.5
  }

  try {
    mapData.clearOverLays()
    // 关闭之前的绘制工具
    if (polygonToolHandler) {
      polygonToolHandler.close()
    }

    // 创建新的绘制工具
    polygonToolHandler = new T.PolygonTool(mapData, config)
    polygonToolHandler.open()

    // 监听绘制完成事件
    polygonToolHandler.addEventListener('draw', (e) => {
      const resultArr = e.currentLnglats
      const drawArr = []
      resultArr.forEach((item: any) => {
        // 将覆盖物返回父级
        drawArr.push({ lng: item.lng, lat: item.lat })
      })
      // 可以在这里处理绘制完成后的数据
      console.log('绘制完成，坐标点:', drawArr)
    })
  } catch (e) {
    console.error('绘制功能初始化失败:', e)
  }
}

// 添加地图覆盖物示例
const addRange = (data) => {
  if (!mapData || !T) {
    console.warn('添加覆盖物功能API不完整')
    return
  }

  try {
    const lngLatArray = data.map((item) => new T.LngLat(item.lng, item.lat))
    const polygon = new T.Polygon(lngLatArray, { color: 'blue', weight: 2, opacity: 0.5 })
    mapData.addOverLay(polygon)
  } catch (e) {
    console.error('添加覆盖物失败:', e)
  }
}

// 地图搜索功能
const searchMap = () => {
  console.log('执行地图搜索功能')
  if (!mapData || !T) {
    console.warn('搜索功能API不完整')
    return
  }

  try {
    const config = {
      pageCapacity: 10,
      onSearchComplete: handleSearchResult
    }

    // 创建搜索对象
    searchMapPoi = new T.LocalSearch(mapData, config)
    searchMapPoi.search(searchText.value, 7)
  } catch (e) {
    console.error('搜索执行失败:', e)
  }
}

// 选择搜索结果
const selectMap = (item: any) => {
  try {
    const [lon, lat] = item.lonlat.split(',')
    const marker = new T.Marker(new T.LngLat(lon, lat))
    mapData.centerAndZoom(new T.LngLat(lon, lat), 14)
    mapData.clearOverLays()
    //向地图上添加标注
    mapData.addOverLay(marker)
    searchSelectData.value = item
    searchResult.value = []
  } catch (e) {
    console.error('选择地图位置失败:', e)
  }
}

// 处理搜索结果
const handleSearchResult = (result) => {
  try {
    searchResult.value = result.getPois()

    // 根据结果类型处理
    switch (parseInt(result.getResultType())) {
      case 1:
        // 解析点数据结果
        handlePoisResult(result.getPois())
        break
      case 2:
        // 解析推荐城市
        handleStatisticsResult(result.getStatistics())
        break
      case 3:
        // 解析行政区划边界
        handleAreaResult(result.getArea())
        break
      case 4:
        // 解析建议词信息
        handleSuggestsResult(result.getSuggests())
        break
    }
  } catch (e) {
    console.error('处理搜索结果失败:', e)
  }
}

// 处理点数据结果
const handlePoisResult = (poisData) => {
  if (!poisData || !mapData || !T) return

  try {
    // 显示搜索列表
    const divMarker = document.createElement('div')
    // 坐标数组，设置最佳比例尺时会用到
    const zoomArr = []

    for (let i = 0; i < poisData.length; i++) {
      // 闭包
      ;(function (i) {
        // 名称
        const name = poisData[i].name
        // 地址
        const address = poisData[i].address
        // 坐标
        const lnglatArr = poisData[i].lonlat.split(',')
        const lnglat = new T.LngLat(lnglatArr[0], lnglatArr[1])

        const winHtml = '名称:' + name + '<br/>地址:' + address

        // 创建标注对象
        const marker = new T.Marker(lnglat)
        // 地图上添加标注点
        mapData.addOverLay(marker)
        // 注册标注点的点击事件
        const markerInfoWin = new T.InfoWindow(winHtml, { autoPan: true })
        marker.addEventListener('click', function () {
          marker.openInfoWindow(markerInfoWin)
        })

        zoomArr.push(lnglat)

        // 在页面上显示搜索的列表
        const a = document.createElement('a')
        a.href = 'javascript://'
        a.innerHTML = name
        a.onclick = function () {
          showPosition(marker, winHtml)
        }
        divMarker.appendChild(document.createTextNode(i + 1 + '.'))
        divMarker.appendChild(a)
        divMarker.appendChild(document.createElement('br'))
      })(i)
    }

    // 显示地图的最佳级别
    if (zoomArr.length > 0) {
      mapData.setViewport(zoomArr)
    }

    // 显示搜索结果统计
    divMarker.appendChild(
      document.createTextNode(
        '共' +
          searchMapPoi.getCountNumber() +
          '条记录，分' +
          searchMapPoi.getCountPage() +
          '页,当前第' +
          searchMapPoi.getPageIndex() +
          '页'
      )
    )
  } catch (e) {
    console.error('处理点数据结果失败:', e)
  }
}

// 处理统计结果（推荐城市）
const handleStatisticsResult = (statisticsData) => {
  if (!statisticsData) return

  try {
    let priorityCitysHtml = ''
    let allAdminsHtml = ''
    const priorityCitys = statisticsData.priorityCitys

    if (priorityCitys) {
      // 推荐城市显示
      priorityCitysHtml += '在中国以下城市有结果<ul>'
      for (let i = 0; i < priorityCitys.length; i++) {
        priorityCitysHtml +=
          '<li>' + priorityCitys[i].name + '(' + priorityCitys[i].count + ')</li>'
      }
      priorityCitysHtml += '</ul>'
    }

    const allAdmins = statisticsData.allAdmins
    if (allAdmins) {
      allAdminsHtml += '更多城市<ul>'
      for (let i = 0; i < allAdmins.length; i++) {
        allAdminsHtml += '<li>' + allAdmins[i].name + '(' + allAdmins[i].count + ')'
        const childAdmins = allAdmins[i].childAdmins
        if (childAdmins) {
          for (let m = 0; m < childAdmins.length; m++) {
            allAdminsHtml +=
              '<blockquote>' + childAdmins[m].name + '(' + childAdmins[m].count + ')</blockquote>'
          }
        }
        allAdminsHtml += '</li>'
      }
      allAdminsHtml += '</ul>'
    }
  } catch (e) {
    console.error('处理统计结果失败:', e)
  }
}

// 处理行政区划边界结果
const handleAreaResult = (areaData) => {
  if (!areaData || !mapData || !T) return

  try {
    if (areaData.points) {
      // 坐标数组，设置最佳比例尺时会用到
      const pointsArr = []
      const points = areaData.points

      for (let i = 0; i < points.length; i++) {
        const regionLngLats = []
        const regionArr = points[i].region.split(',')

        for (let m = 0; m < regionArr.length; m++) {
          const lnglatArr = regionArr[m].split(' ')
          const lnglat = new T.LngLat(lnglatArr[0], lnglatArr[1])
          regionLngLats.push(lnglat)
          pointsArr.push(lnglat)
        }

        // 创建线对象
        const line = new T.Polyline(regionLngLats, {
          color: 'blue',
          weight: 3,
          opacity: 1,
          lineStyle: 'dashed'
        })
        // 向地图上添加线
        mapData.addOverLay(line)
      }

      // 显示最佳比例尺
      if (pointsArr.length > 0) {
        mapData.setViewport(pointsArr)
      }
    }

    if (areaData.lonlat) {
      const regionArr = areaData.lonlat.split(',')
      mapData.panTo(new T.LngLat(regionArr[0], regionArr[1]))
    }
  } catch (e) {
    console.error('处理行政区划边界结果失败:', e)
  }
}

// 处理建议词信息
const handleSuggestsResult = (suggestsData) => {
  if (!suggestsData) return

  try {
    // 建议词提示，如果搜索类型为公交规划建议词或公交站搜索时，返回结果为公交信息的建议词。
    let suggestsHtml = '建议词提示<ul>'
    for (let i = 0; i < suggestsData.length; i++) {
      suggestsHtml +=
        '<li>' +
        suggestsData[i].name +
        "&nbsp;&nbsp;<font style='color:#666666'>" +
        suggestsData[i].address +
        '</font></li>'
    }
    suggestsHtml += '</ul>'
  } catch (e) {
    console.error('处理建议词信息失败:', e)
  }
}

// 显示位置信息（搜索结果列表中的点击事件）
const showPosition = (marker, winHtml) => {
  if (marker && marker.openInfoWindow) {
    const markerInfoWin = new T.InfoWindow(winHtml, { autoPan: true })
    marker.openInfoWindow(markerInfoWin)
  }
}

// 清理地图所有资源
const clearMap = () => {
  setTimeout(() => {
    if (mapData) {
      // 先移除右键菜单
      if (mapData.removeContextMenu && typeof mapData.removeContextMenu === 'function') {
        try {
          mapData.removeContextMenu()
        } catch (e) {
          console.warn('清理右键菜单失败:', e)
        }
      }
      // 清理覆盖物
      mapData.clearOverLays()
    }
  }, 300)
}
// 卸载地图
const unmountMap = () => {
  if (mapData) {
    console.log('清理地图覆盖物')
    try {
      // 清理地图覆盖物
      if (mapData.clearOverlays && typeof mapData.clearOverlays === 'function') {
        mapData.clearOverlays()
        console.log('清理地图覆盖物1111')
      }

      // 销毁地图
      if (mapData.destroy && typeof mapData.destroy === 'function') {
        mapData.destroy()
        console.log('清理地图覆盖物222')
      }
    } catch (e) {
      console.warn('销毁地图时出错:', e)
    }
  }
}

// 清理通学车辆标点（不清空整图覆盖物）
const clearMarkers = () => {
  try {
    clearSchoolBusOverlays()
    clearRideVehicleOverlays()
    rideVehiclePointsCache = []
    rideVehicleLastZoom = null
    if (rideVehicleZoomTimer) {
      clearTimeout(rideVehicleZoomTimer)
      rideVehicleZoomTimer = null
    }
    if (rideVehicleZoomPollTimer) {
      clearInterval(rideVehicleZoomPollTimer)
      rideVehicleZoomPollTimer = null
    }
    rideVehicleZoomBound = false
  } catch (e) {
    console.warn('清理地图标点时出错:', e)
  }
}

// 组件挂载时确保天地图 SDK 可用（index.html 已全局引入；此处按需兜底加载）
onMounted(async () => {
  try {
    await loadTiandituSdk()
    await nextTick()
    // 等待容器完成布局后再初始化，避免高度为 0 导致白屏
    setTimeout(() => {
      initMap()
    }, 200)
  } catch (e) {
    console.error('天地图 SDK 加载失败，在途监控地图无法渲染', e)
  }
})

// 组件卸载前清理
onBeforeUnmount(() => {
  if (rideVehicleZoomTimer) {
    clearTimeout(rideVehicleZoomTimer)
    rideVehicleZoomTimer = null
  }
  if (rideVehicleZoomPollTimer) {
    clearInterval(rideVehicleZoomPollTimer)
    rideVehicleZoomPollTimer = null
  }
  rideVehicleZoomBound = false
  rideVehiclePointsCache = []
  if (mapData) {
    try {
      unbindLeftClickMarker()
      // 清理地图覆盖物
      if (mapData.clearOverlays && typeof mapData.clearOverlays === 'function') {
        mapData.clearOverlays()
      }

      // 关闭绘制工具
      if (polygonToolHandler) {
        polygonToolHandler.close()
      }
    } catch (e) {
      console.warn('清理地图资源时出错:', e)
    }

    // 清理引用
    mapData = null
    T = null
    searchMapPoi = null
    polygonToolHandler = null
  }
})

// 暴露方法给父组件
defineExpose({
  drawingRange,
  addRange,
  setMarkerEcho,
  setSchoolBusMarkerEcho,
  setSchoolBusMarkers,
  setRideVehicleMarkers,
  centerAndZoomTo,
  afterMapViewSettled,
  clearMap,
  unmountMap,
  getCenter,
  clearMarkers,
  /** 兼容原 TencentMap API */
  createMarker,
  clearMarker,
  setMapCenter
})
</script>

<style scoped lang="less">
/* isolation 将天地图控件/瓦片的 z-index 限制在地图容器内，避免盖住全局 Dialog 遮罩 */
.tmap {
  position: relative;
  z-index: 0;
  width: 100%;
  height: 100%;
  min-height: 350px;
  isolation: isolate;
}

.tian-di-tu-map {
  width: 100%;
  height: 100%;
  min-height: 350px;
}

.search {
  position: absolute;
  top: 10px;
  left: 65px;
  z-index: 999;
}

.search_result {
  height: 285px;
  max-width: 300px;
  overflow: auto;
  background-color: #fff;

  .search_box {
    display: flex;
    padding: 5px;
    border-bottom: 1px solid gainsboro;
    justify-content: space-between;
    align-items: center;
  }

  .search_name {
    display: block;
    font-size: 14px;
    font-weight: bold;
  }

  .search_address {
    font-size: 12px;
    color: #666;
  }
}

.pain_map {
  position: absolute;
  top: 10px;
  right: 30px;
  z-index: 999;
}
</style>
